import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { api } from "../../Config/Api";
import { AppType } from "../../types/appType";
import { ErrorType } from "../../types/errorType";
import { RootState } from "../store";
import { ExtractErrorInfo } from "../../components/shared/ExtractErrorInfo";
import { API_ENDPOINTS } from "Config/apiConfig";
import { IncidentHistoryType } from "types/incidentHistoryType";
import { HistoryMessageBuilder } from "utils/historyMessageBuilders/index";

export interface AppTypeWithIncidentHistoryType extends AppType {
  incidentHistory: IncidentHistoryType[];
  loadingPin?: boolean;
  errorPin?: ErrorType | null;
  errorHighlight?: ErrorType | null;
  highlightingId?: string | null;
}

const initialState: AppTypeWithIncidentHistoryType = {
  loading: false,
  error: null,
  incidentHistory: [],
  loadingPin: false,
  errorPin: null,
  errorHighlight: null,
  highlightingId: null,
};

// Thunk to fetch incident history by Incident ID
export const fetchIncidentHistoryByIncidentId = createAsyncThunk<IncidentHistoryType[], string, { rejectValue: any }>(
  "incidentHistory/fetchIncidentHistoryByIncidentId",
  async (incidentId, { rejectWithValue }) => {
    try {
      const { data } = await api.get(`${API_ENDPOINTS.incidentHistory(incidentId)}`);

      // Process entries to construct formatted messages from structured data
      const processedEntries = data.results.map((entry: IncidentHistoryType) => {
        const processedEntry: IncidentHistoryType = { ...entry };

        // Pass entry to builder to handle routing and formatting
        const message = HistoryMessageBuilder.buildMessage(processedEntry);
        if (message.length > 0) {
          processedEntry.message = message;
        }

        return processedEntry;
      });

      return processedEntries;
    } catch (error: any) {
      const errorInfo = ExtractErrorInfo(error);
      return rejectWithValue(errorInfo);
    }
  }
);

// pin or unpin incident history item by incident ID and incident history ID
export const postIncidentHistoryPin = createAsyncThunk<
  { pin: boolean },
  { incidentId: string; incidentHistoryId: string; pin: boolean },
  { rejectValue: any }
>("incidentHistoryPin/postIncidentHistoryPin", async ({ incidentId, incidentHistoryId, pin }, { rejectWithValue }) => {
  try {
    const url = API_ENDPOINTS.setIncidentHistoryPin(incidentId, incidentHistoryId);
    await api.post(url, { pin });
    return { pin };
  } catch (error: any) {
    const errorInfo = ExtractErrorInfo(error);
    return rejectWithValue(errorInfo);
  }
});

// highlight or unhighlight incident history item by incident ID and incident history ID
export const postIncidentHistoryHighlight = createAsyncThunk<
  { highlight: boolean },
  { incidentId: string; incidentHistoryId: string; highlight: boolean },
  { rejectValue: any }
>(
  "incidentHistory/postIncidentHistoryHighlight",
  async ({ incidentId, incidentHistoryId, highlight }, { rejectWithValue }) => {
    try {
      const url = API_ENDPOINTS.setIncidentHistoryHighlight(incidentId, incidentHistoryId);
      await api.post(url, { highlight });
      return { highlight };
    } catch (error: any) {
      const errorInfo = ExtractErrorInfo(error);
      return rejectWithValue(errorInfo);
    }
  }
);

const incidentHistorySlice = createSlice({
  name: "incidentHistory",
  initialState,
  reducers: {
    clearIncidentHistory: (state) => {
      state.incidentHistory = [];
      state.loading = false;
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder.addCase(fetchIncidentHistoryByIncidentId.fulfilled, (state, action) => {
      state.incidentHistory = action.payload;
      state.loading = false;
    });
    builder.addCase(fetchIncidentHistoryByIncidentId.pending, (state) => {
      state.loading = true;
      state.error = null;
    });
    builder.addCase(fetchIncidentHistoryByIncidentId.rejected, (state, action) => {
      state.loading = false;
      state.error = action.payload as ErrorType;
    });
    builder.addCase(postIncidentHistoryPin.pending, (state) => {
      state.loadingPin = true;
      state.errorPin = null;
    });
    builder.addCase(postIncidentHistoryPin.fulfilled, (state, action) => {
      state.loadingPin = false;
      // action.meta.arg.incidentHistoryId is the id to update
      const { incidentHistoryId } = action.meta.arg;
      const pin = action.payload;
      const entry = state.incidentHistory.find((e) => e.id === incidentHistoryId);
      if (entry) {
        entry.pinned = pin.pin;
      }
    });
    builder.addCase(postIncidentHistoryPin.rejected, (state, action) => {
      state.loadingPin = false;
      state.errorPin = action.payload as ErrorType;
    });
    builder.addCase(postIncidentHistoryHighlight.pending, (state, action) => {
      state.errorHighlight = null;
      state.highlightingId = action.meta.arg.incidentHistoryId;
    });
    builder.addCase(postIncidentHistoryHighlight.fulfilled, (state, action) => {
      state.highlightingId = null;
      // action.meta.arg.incidentHistoryId is the id to update
      const { incidentHistoryId } = action.meta.arg;
      const { highlight } = action.payload;
      const entry = state.incidentHistory.find((e) => e.id === incidentHistoryId);
      if (entry) {
        entry.highlighted = highlight;
      }
    });
    builder.addCase(postIncidentHistoryHighlight.rejected, (state, action) => {
      state.highlightingId = null;
      state.errorHighlight = action.payload as ErrorType;
    });
  },
});

export const { clearIncidentHistory } = incidentHistorySlice.actions;

export const incidentHistoryRootState = (state: RootState) => state.incidentHistory;
export default incidentHistorySlice.reducer;
